import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import { Input, Select } from '../ui/Input'
import { CustomerOrProspectLookup } from '../ui/CustomerOrProspectLookup'
import { Pagination } from '../ui/Pagination'
import { dataService } from '../../services/DataService'
import { QuotationLinesView } from './QuotationLinesView'
import { useBackendAuth } from '../auth/BackendAuthProvider'

// mserp_alg_salesquotationorigin picklist (Dataverse option set values)
const QUOTATION_ORIGINS = [
  { value: '200000000', key: 'manual',
    dotClass: 'bg-gray-500', selectedClass: 'bg-gray-100 border-gray-500 text-gray-900',
    tagClass: 'bg-gray-100 text-gray-700 border-gray-300' },
  { value: '200000001', key: 'salesPortal',
    dotClass: 'bg-blue-500', selectedClass: 'bg-blue-50 border-blue-500 text-blue-900',
    tagClass: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: '200000002', key: 'easyvent',
    dotClass: 'bg-purple-500', selectedClass: 'bg-purple-50 border-purple-500 text-purple-900',
    tagClass: 'bg-purple-100 text-purple-700 border-purple-200' }
]
const ORIGIN_PILL_BASE = 'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-sm border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1'
const ORIGIN_PILL_IDLE = 'bg-white border-gray-300 text-gray-700 font-medium hover:border-gray-400 hover:bg-gray-50'
const getOriginMeta = (value) => QUOTATION_ORIGINS.find(o => o.value === String(value ?? ''))
const getQuotationName = (quotation) => quotation?.mserp_alg_salesquotationname || quotation?.mserp_salesquotationname || ''

export const QuotesListView = ({ initialView, onViewChange, preserveUrlParams = false, allowQuotationDuplicate = true, hideEmailFilter = false }) => {
  const { t } = useTranslation(['quotation', 'common'])
  const { smmParameters, dataVersion } = useBackendAuth()
  const isAutoSequenceEnabled = smmParameters?.mserp_alg_automaticseqnum === 200000001
  const canSetManualQuotationNumber = !isAutoSequenceEnabled
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedQuotationNumber, setSelectedQuotationNumber] = useState(null)
  const [duplicatingQuote, setDuplicatingQuote] = useState(null)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)
  const [duplicationProgress, setDuplicationProgress] = useState('')
  const [duplicateError, setDuplicateError] = useState(null) // Error specific to duplicate dialog
  const [duplicateCustomerAccount, setDuplicateCustomerAccount] = useState('')
  const [duplicateCustomerName, setDuplicateCustomerName] = useState('')
  const [duplicateIsNewCustomerOrProspect, setDuplicateIsNewCustomerOrProspect] = useState(false)
  const [isResolvingDuplicateCustomer, setIsResolvingDuplicateCustomer] = useState(false)
  const [duplicateQuotationNumber, setDuplicateQuotationNumber] = useState('') // Quotation number (max 20 chars)
  const [duplicateQuotationName, setDuplicateQuotationName] = useState('')
  const [duplicateSelectedType, setDuplicateSelectedType] = useState(null) // Track if customer or prospect
  const [duplicateRecalculatePrices, setDuplicateRecalculatePrices] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 3,
    totalCount: 50,
    hasNext: true,
    hasPrevious: false
  })

  const getInitialSearch = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('searchTerm') || urlParams.get('search') || ''
  }

  const [filters, setFilters] = useState({
    search: getInitialSearch(),
    status: '',
    dateFrom: '',
    dateTo: ''
  })
  const [emailFilter, setEmailFilter] = useState('')
  // Initialize state based on URL parameters or passed initialView prop to avoid flash
  const getInitialView = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const hasOpportunityFilter = Boolean(urlParams.get('opportunity') || urlParams.get('opportunityDetail'))
    // First check if there's an initialView prop passed from parent
    if (initialView) {
      // Embedded list URLs with an opportunity use the Opportunity tab.
      return initialView === 'list' && hasOpportunityFilter
        ? 'opportunity'
        : initialView
    }
    // Then check URL parameters
    return hasOpportunityFilter ? 'opportunity' : 'open'
  }

  const getInitialOpportunityFilter = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('opportunityDetail') || urlParams.get('opportunity') || ''
  }

  const [currentView, setCurrentView] = useState(getInitialView()) // 'open', 'closed', 'followup', 'expiry', or 'opportunity'
  // Origin filter (mserp_alg_salesquotationorigin): '' = all, or one of the picklist values below.
  // Combined (AND) with the view above, so Open/Closed/... are also restricted by origin.
  const [originFilter, setOriginFilter] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const value = urlParams.get('origin') || ''
    return QUOTATION_ORIGINS.some(o => o.value === value) ? value : ''
  })
  const [opportunityFilter, setOpportunityFilter] = useState(getInitialOpportunityFilter()) // For filtering by opportunity ID
  const [opportunityDetail, setOpportunityDetail] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('opportunityDetail') || ''
  })
  const opportunityDetailOpenedRef = useRef(false)
  const [availableOpportunities, setAvailableOpportunities] = useState([]) // List of available opportunity IDs
  const [expandedOpportunities, setExpandedOpportunities] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const opportunityDetail = urlParams.get('opportunityDetail')
    return opportunityDetail ? new Set([opportunityDetail]) : new Set()
  }) // Track expanded opportunity groups
  const [openDropdown, setOpenDropdown] = useState(null) // Track which dropdown is open
  const [deletingQuote, setDeletingQuote] = useState(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deletionProgress, setDeletionProgress] = useState('')

  // Effect to handle initialView changes from parent
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const hasOpportunityDetail = Boolean(urlParams.get('opportunityDetail'))
    const requestedView = initialView === 'list' && hasOpportunityDetail
      ? 'opportunity'
      : initialView

    if (requestedView && requestedView !== currentView) {
      setCurrentView(requestedView)
      // Notify parent that we've consumed the initialView
      if (onViewChange) {
        onViewChange()
      }
    }
  }, [initialView])

  const loadQuotes = async (resetData = false) => {
    setLoading(true)
    setError(null)

    try {
      let filter = ''
      const filterParts = []

      if (filters.search) {
        filterParts.push(`contains(mserp_alg_salesquotationname, '${filters.search}') or contains(mserp_salesquotationname, '${filters.search}') or contains(mserp_salesquotationnumber, '${filters.search}') or contains(mserp_requestingcustomeraccountnumber, '${filters.search}')`)
      }

      // Add view-based filtering for open vs closed vs followup vs expiry vs opportunity quotations
      if (currentView === 'open') {
        // Open: Portal status Draft (200000000), Sent to ERP (200000001), Sent (200000002)
        filterParts.push(`(mserp_alg_portalstatus eq 200000000 or mserp_alg_portalstatus eq 200000001 or mserp_alg_portalstatus eq 200000002)`)
        // Standard status: Created (200000000), Sent (200000001), Confirmed (200000002)
        filterParts.push(`(mserp_salesquotationstatus eq 200000000 or mserp_salesquotationstatus eq 200000001 or mserp_salesquotationstatus eq 200000002)`)
      } else if (currentView === 'closed') {
        // Closed: Portal status Accepted (200000003), Rejected (200000004), Cancelled (200000005)
        filterParts.push(`(mserp_alg_portalstatus eq 200000003 or mserp_alg_portalstatus eq 200000004 or mserp_alg_portalstatus eq 200000005)`)
        // Standard status: Lost (200000003), Cancelled (200000004), or higher
        filterParts.push(`(mserp_salesquotationstatus eq 200000003 or mserp_salesquotationstatus eq 200000004 or mserp_salesquotationstatus eq 200000005 or mserp_salesquotationstatus eq 200000006 or mserp_salesquotationstatus eq 200000007 or mserp_salesquotationstatus eq 200000008 or mserp_salesquotationstatus eq 200000009)`)
      } else if (currentView === 'followup') {
        // Follow-up: Open quotes with follow-up date in current week or before
        // Open portal status filter
        filterParts.push(`(mserp_alg_portalstatus eq 200000000 or mserp_alg_portalstatus eq 200000001 or mserp_alg_portalstatus eq 200000002)`)
        // Open standard status filter
        filterParts.push(`(mserp_salesquotationstatus eq 200000000 or mserp_salesquotationstatus eq 200000001 or mserp_salesquotationstatus eq 200000002)`)

        // Calculate end of current week (Sunday)
        const today = new Date()
        const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, etc.
        const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek
        const endOfWeek = new Date(today)
        endOfWeek.setDate(today.getDate() + daysUntilSunday)
        endOfWeek.setHours(23, 59, 59, 999)
        const endOfWeekStr = endOfWeek.toISOString().split('T')[0]

        // Follow-up date is this week or before
        filterParts.push(`mserp_salesquotationfollowupdate le ${endOfWeekStr}`)
        // Exclude quotes where expiry date is before today (already expired)
        const todayStr = new Date().toISOString().split('T')[0]
        filterParts.push(`mserp_salesquotationexpirydate ge ${todayStr}`)
      } else if (currentView === 'expiry') {
        // Expiry: Open quotes with expiry date today or before
        // Open portal status filter
        filterParts.push(`(mserp_alg_portalstatus eq 200000000 or mserp_alg_portalstatus eq 200000001 or mserp_alg_portalstatus eq 200000002)`)
        // Open standard status filter
        filterParts.push(`(mserp_salesquotationstatus eq 200000000 or mserp_salesquotationstatus eq 200000001 or mserp_salesquotationstatus eq 200000002)`)

        // Expiry date is today or earlier
        const todayStr = new Date().toISOString().split('T')[0]
        filterParts.push(`mserp_salesquotationexpirydate le ${todayStr}`)
      } else if (currentView === 'opportunity') {
        // Opportunity view: show ALL quotations (Open + Closed) that have an opportunity ID
        filterParts.push(`mserp_alg_portalopportunity ne null and mserp_alg_portalopportunity ne ''`)

        // If opportunity filter is specified, filter by exact opportunity ID match
        if (opportunityFilter.trim()) {
          filterParts.push(`mserp_alg_portalopportunity eq '${opportunityFilter.trim()}'`)
        }
      }

      // Note: Additional status filter if needed
      // if (filters.status) {
      //   filterParts.push(`mserp_alg_portalstatus eq '${filters.status}'`)
      // }

      // if (filters.dateFrom) {
      //   filterParts.push(`createdon ge ${filters.dateFrom}T00:00:00Z`)
      // }

      // if (filters.dateTo) {
      //   filterParts.push(`createdon le ${filters.dateTo}T23:59:59Z`)
      // }

      // Origin filter applies on top of every view
      if (originFilter) {
        filterParts.push(`mserp_alg_salesquotationorigin eq ${originFilter}`)
      }

      if (filterParts.length > 0) {
        filter = filterParts.join(' and ')
      }

      // Use real Dataverse data - simplified without server-side pagination
      const getQuotations = hideEmailFilter
        ? dataService.getMyQuotationsPaginated.bind(dataService)
        : dataService.getQuotationsPaginated.bind(dataService)
      const result = hideEmailFilter
        ? await getQuotations(1, 500, filter, '')
        : await getQuotations(1, 500, filter, '', emailFilter)

      if (result.success) {
        setQuotes(result.data)
        if (opportunityDetail && !opportunityDetailOpenedRef.current) {
          const firstOpportunityQuote = result.data.find(quote => quote.mserp_alg_portalopportunity === opportunityDetail)
          if (firstOpportunityQuote?.mserp_salesquotationnumber) {
            setSelectedQuotationNumber(firstOpportunityQuote.mserp_salesquotationnumber)
            opportunityDetailOpenedRef.current = true
          }
        }
        setPagination({
          currentPage: result.currentPage,
          pageSize: result.pageSize,
          totalPages: result.totalPages,
          totalCount: result.totalCount,
          hasNext: result.hasNext,
          hasPrevious: result.hasPrevious
        })

        // Extract available opportunities for the dropdown
        if (currentView === 'opportunity') {
          const opportunities = result.data
            .map(quote => quote.mserp_alg_portalopportunity)
            .filter(opportunity => opportunity && opportunity.trim() !== '')
            .filter((opportunity, index, self) => self.indexOf(opportunity) === index) // Remove duplicates
            .sort()
          setAvailableOpportunities(opportunities)
        } else {
          setAvailableOpportunities([])
        }
      } else {
        setError(result.error || t('common:errors.generic'))
      }
    } catch (err) {
      setError(err.message || t('common:errors.generic'))
    } finally {
      setLoading(false)
    }
  }

  // Handle URL parameters on component mount and check periodically for changes
  useEffect(() => {
    const checkUrlParameters = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const opportunityParam = urlParams.get('opportunity')
      const opportunityDetailParam = urlParams.get('opportunityDetail')
      const searchTerm = urlParams.get('searchTerm') || urlParams.get('search')
      const requestedOpportunity = opportunityDetailParam || opportunityParam

      console.log('QuotesListView: Checking URL parameters:', {
        opportunityParam,
        currentURL: window.location.href,
        currentView,
        currentOpportunityFilter: opportunityFilter
      })

      if (requestedOpportunity && requestedOpportunity !== opportunityFilter) {
        console.log('QuotesListView: Setting opportunity filter:', requestedOpportunity)
        setCurrentView('opportunity')
        setOpportunityFilter(requestedOpportunity)

        if (!preserveUrlParams) {
          // Preserve the existing dashboard behavior for non-embedded views.
          setTimeout(() => {
            const newUrl = window.location.pathname
            window.history.replaceState({}, document.title, newUrl)
          }, 200)
        }
      } else if (!requestedOpportunity && currentView === 'opportunity' && opportunityFilter) {
        // Keep the filter active even after URL is cleaned
        console.log('QuotesListView: URL cleaned but keeping opportunity filter active')
      }

      if (opportunityDetailParam) {
        setExpandedOpportunities(prev => new Set(prev).add(opportunityDetailParam))
      }

      if (searchTerm) {
        setFilters(prev => prev.search === searchTerm ? prev : { ...prev, search: searchTerm })
      }
    }

    // Check immediately
    checkUrlParameters()

    // Also check after a short delay to catch URL changes from auth redirects
    const timeoutId = setTimeout(checkUrlParameters, 100)

    return () => clearTimeout(timeoutId)
  }, []) // Only run on mount

  // Listen for URL restoration after authentication
  useEffect(() => {
    const handleUrlRestored = (event) => {
      console.log('QuotesListView: URL restored after auth, checking parameters:', event.detail.params)

      const urlParams = new URLSearchParams(window.location.search)
      const opportunityParam = urlParams.get('opportunity')
      const opportunityDetailParam = urlParams.get('opportunityDetail')

      const searchTerm = urlParams.get('searchTerm') || urlParams.get('search')

      if (searchTerm) {
        setFilters(prev => prev.search === searchTerm ? prev : { ...prev, search: searchTerm })
      }

      if (opportunityParam) {
        console.log('QuotesListView: Applying restored opportunity filter:', opportunityParam)
        setCurrentView('opportunity')
        setOpportunityFilter(opportunityParam)

        if (!preserveUrlParams) {
          setTimeout(() => {
            const newUrl = window.location.pathname
            window.history.replaceState({}, document.title, newUrl)
          }, 100)
        }
      }

      if (opportunityDetailParam) {
        setCurrentView('opportunity')
        setOpportunityFilter(opportunityDetailParam)
        setOpportunityDetail(opportunityDetailParam)
        opportunityDetailOpenedRef.current = false
        setExpandedOpportunities(prev => new Set(prev).add(opportunityDetailParam))
      }
    }

    window.addEventListener('urlRestored', handleUrlRestored)

    return () => {
      window.removeEventListener('urlRestored', handleUrlRestored)
    }
  }, [])

  useEffect(() => {
    loadQuotes(1, true)

    // Preserve embedded filters so the iframe can be reloaded safely.
    if (!preserveUrlParams && currentView === 'opportunity' && opportunityFilter) {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.has('opportunity') || urlParams.has('opportunityDetail')) {
        console.log('QuotesListView: Cleaning URL during quotes load')
        const newUrl = window.location.pathname
        window.history.replaceState({}, document.title, newUrl)
      }
    }
  }, [filters, emailFilter, currentView, originFilter, opportunityFilter, opportunityDetail, dataVersion, preserveUrlParams])

  // Embedded URLs retain their filters so they can be reloaded safely.


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown !== null && !event.target.closest('.relative')) {
        setOpenDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdown])

  const handlePageChange = (newPage) => {
    loadQuotes(newPage)
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    })
    setEmailFilter('')
    setOriginFilter('')
    // Also clear opportunity filter
    setOpportunityFilter('')
    setOpportunityDetail('')
    opportunityDetailOpenedRef.current = false
    setExpandedOpportunities(new Set())
  }

  const handleDuplicateQuote = (quote) => {
    if (!allowQuotationDuplicate) return

    setDuplicatingQuote(quote)
    setDuplicateCustomerName('')
    setDuplicateIsNewCustomerOrProspect(false)
    setIsResolvingDuplicateCustomer(false)

    // Set customer or prospect based on what the original quotation has
    if (quote.mserp_requestingcustomeraccountnumber) {
      setDuplicateCustomerAccount(quote.mserp_requestingcustomeraccountnumber)
      setDuplicateSelectedType('customer')
    } else if (quote.mserp_prospectid) {
      setDuplicateCustomerAccount(quote.mserp_prospectid)
      setDuplicateSelectedType('prospect')
    } else {
      setDuplicateCustomerAccount('')
      setDuplicateSelectedType(null)
    }

    // Pre-fill with original quotation number + "_Dup" suffix (max 20 chars total)
    const originalNumber = quote.mserp_salesquotationnumber || ''
    const originalName = getQuotationName(quote)
    const duplicateSuffix = '_Dup'

    // Ensure quotation number doesn't exceed 20 characters
    let duplicateNumber = ''
    if (canSetManualQuotationNumber && originalNumber) {
      if (originalNumber.length + duplicateSuffix.length <= 20) {
        // Fits within 20 chars - add suffix
        duplicateNumber = originalNumber + duplicateSuffix
      } else {
        // Trim original number to fit suffix
        const maxOriginalLength = 20 - duplicateSuffix.length
        duplicateNumber = originalNumber.substring(0, maxOriginalLength) + duplicateSuffix
      }
    }

    // Add "_Dup" suffix to quotation name (no length limit)
    const duplicateName = originalName ? originalName + duplicateSuffix : ''

    setDuplicateQuotationNumber(duplicateNumber)
    setDuplicateQuotationName(duplicateName)
    setDuplicateRecalculatePrices(false)
    setShowDuplicateDialog(true)
  }

  const confirmDuplicateQuote = async () => {
    if (!duplicatingQuote || !duplicateCustomerAccount || !duplicateCustomerName.trim() || isResolvingDuplicateCustomer || (canSetManualQuotationNumber && !duplicateQuotationNumber.trim()) || !duplicateQuotationName.trim()) return

    const originalNumber = duplicatingQuote.mserp_salesquotationnumber || ''
    const originalName = getQuotationName(duplicatingQuote)

    if (canSetManualQuotationNumber && duplicateQuotationNumber.trim() === originalNumber) {
      setDuplicateError(t('quotation:duplicate.errorSameNumber'))
      return
    }

    if (duplicateQuotationName.trim() === originalName) {
      setDuplicateError(t('quotation:duplicate.errorSameName'))
      return
    }

    try {
      setLoading(true)
      setDuplicateError(null) // Clear any previous errors
      setDuplicationProgress(t('quotation:duplicate.progressHeader'))

      // Small delay to show the progress message
      await new Promise(resolve => setTimeout(resolve, 500))

      setDuplicationProgress(t('quotation:duplicate.progressLines'))

      // Create clean quotation data like the create quotation process
      const quotationToCreate = {
        ...(canSetManualQuotationNumber && { mserp_salesquotationnumber: duplicateQuotationNumber }),
        mserp_salesquotationname: duplicateIsNewCustomerOrProspect ? duplicateQuotationName : duplicateCustomerName,
        mserp_alg_salesquotationname: duplicateQuotationName,
        // Use customer or prospect field based on selection
        ...(duplicateSelectedType === 'customer'
          ? { mserp_requestingcustomeraccountnumber: duplicateCustomerAccount }
          : { mserp_prospectid: duplicateCustomerAccount }
        ),
        // Copy optional fields from original if they exist
        ...(duplicatingQuote.mserp_contactpersonid && { mserp_contactpersonid: duplicatingQuote.mserp_contactpersonid }),
        ...(duplicatingQuote.mserp_alg_portalopportunity && { mserp_alg_portalopportunity: duplicatingQuote.mserp_alg_portalopportunity }),
        // Set initial status to Draft
        mserp_salesquotationstatus: 200000000
      }

      // Handle dates using SMM parameters
      const now = new Date()

      // Get days from SMM parameters, fallback to hardcoded values
      const followUpDays = smmParameters?.mserp_alg_followupdate || 180 // Default: 6 months (180 days)
      const expiryDays = smmParameters?.mserp_alg_expirydate || 30 // Default: 30 days

      // Set follow-up date to today + configured days
      const followUpDate = new Date(now)
      followUpDate.setDate(now.getDate() + followUpDays)
      quotationToCreate.mserp_salesquotationfollowupdate = followUpDate.toISOString()

      console.log(`Duplicate: Using SMM parameters - followUpDays: ${followUpDays}, expiryDays: ${expiryDays}`)

      // Set expiry date to today + configured days (or copy if original is in future)
      if (duplicatingQuote.mserp_salesquotationexpirydate) {
        const expiryDate = new Date(duplicatingQuote.mserp_salesquotationexpirydate)
        if (expiryDate > now) {
          // Keep original expiry date if it's in the future
          quotationToCreate.mserp_salesquotationexpirydate = duplicatingQuote.mserp_salesquotationexpirydate
        } else {
          // Set new expiry date using SMM parameters
          const newExpiryDate = new Date(now)
          newExpiryDate.setDate(now.getDate() + expiryDays)
          quotationToCreate.mserp_salesquotationexpirydate = newExpiryDate.toISOString()
        }
      } else {
        // No expiry date in original, set using SMM parameters
        const newExpiryDate = new Date(now)
        newExpiryDate.setDate(now.getDate() + expiryDays)
        quotationToCreate.mserp_salesquotationexpirydate = newExpiryDate.toISOString()
      }

      // Create the new quotation header (same as create quotation process)
      const headerResult = await dataService.createQuotation(quotationToCreate)

      if (!headerResult.success) {
        throw new Error(headerResult.error || t('quotation:list.failedToDuplicate'))
      }

      console.log('Quotation header created successfully:', headerResult.data)

      // Now duplicate the lines from original quotation
      const originalQuotationNumber = duplicatingQuote.mserp_salesquotationnumber
      const newQuotationNumber = headerResult.data?.mserp_salesquotationnumber

      if (newQuotationNumber && originalQuotationNumber) {
        setDuplicationProgress(t('quotation:duplicate.progressLines'))

        // Get original quotation lines
        const linesResult = await dataService.getQuotationLines(originalQuotationNumber)

        if (linesResult.success && linesResult.data && linesResult.data.length > 0) {
          console.log(`Duplicating ${linesResult.data.length} lines...`)

          // Add each line to the new quotation
          for (const line of linesResult.data) {
            // Create clean line data without system fields
            const cleanLineData = {
              mserp_itemnumber: line.mserp_itemnumber,
              mserp_itemname: line.mserp_itemname,
              mserp_salesquantity: line.mserp_salesquantity,
              mserp_salesunit: line.mserp_salesunit,
              ...(!duplicateRecalculatePrices && {
                mserp_salesprice: line.mserp_salesprice,
                mserp_lineamount: line.mserp_lineamount,
                mserp_linepercent: line.mserp_linepercent,
                mserp_alg_defaultnetsalesprice: line.mserp_alg_defaultnetsalesprice,
                mserp_alg_netsalesprice: line.mserp_alg_netsalesprice,
                mserp_alg_variationpercentage: line.mserp_alg_variationpercentage,
                mserp_linediscountpercentage: line.mserp_linediscountpercentage
              }),
              mserp_alg_producttype: line.mserp_alg_producttype,
              // Missing fields that should be duplicated
              mserp_alg_reportdisplayorder: line.mserp_alg_reportdisplayorder, // Ord
              mserp_alg_outofcatalogproduct: line.mserp_alg_outofcatalogproduct, // Out of Catalog
              mserp_requestedsalesquantity: line.mserp_requestedsalesquantity, // Requested Qty
              mserp_alg_grouping: line.mserp_alg_grouping, // Grouping
              // Add other line fields as needed
              ...(line.mserp_externalitemnumber && { mserp_externalitemnumber: line.mserp_externalitemnumber }),
              ...(line.mserp_alg_flow && { mserp_alg_flow: line.mserp_alg_flow }),
              ...(line.mserp_alg_pressure && { mserp_alg_pressure: line.mserp_alg_pressure }),
              ...(line.mserp_alg_graderpm && { mserp_alg_graderpm: line.mserp_alg_graderpm })
            }

            await dataService.addQuotationLine(newQuotationNumber, cleanLineData)
          }
        }
      }

      const result = {
        success: true,
        data: headerResult.data
      }

      if (!result.success) {
        throw new Error(result.error || t('quotation:list.failedToDuplicate'))
      }

      console.log('Quotation duplicated successfully:', result.data)

      setDuplicationProgress(t('quotation:duplicate.progressRefresh'))

      // Refresh the quotes list to show the new duplicate
      await loadQuotes(pagination.currentPage)

      // Try to find and open the newly duplicated quotation
      if (result.data && result.data.mserp_salesquotationnumber) {
        console.log('Opening newly duplicated quotation:', result.data.mserp_salesquotationnumber)
        setSelectedQuotationNumber(result.data.mserp_salesquotationnumber)
      } else {
        // Fallback: try to find the duplicated quotation by matching criteria
        console.log('Attempting to find newly duplicated quotation...')
        setDuplicationProgress(t('quotation:duplicate.progressLooking'))

        try {
          // Wait a moment for the system to process
          await new Promise(resolve => setTimeout(resolve, 1000))

          // Get fresh quotation list
          const quotationsResult = await dataService.getQuotationsPaginated(1, 20, '', '')

          if (quotationsResult.success && quotationsResult.data.length > 0) {
            // Look for quotation with similar name but different number
            const originalNumber = duplicatingQuote.mserp_salesquotationnumber

            // Find quotations with similar name but different number (likely duplicates)
            const possibleDuplicates = quotationsResult.data.filter(q =>
              getQuotationName(q) === duplicateQuotationName &&
              q.mserp_salesquotationnumber !== originalNumber
            )

            if (possibleDuplicates.length > 0) {
              // Sort by quotation number (assuming newer ones have higher numbers)
              const sortedDuplicates = possibleDuplicates.sort((a, b) =>
                a.mserp_salesquotationnumber.localeCompare(b.mserp_salesquotationnumber)
              )

              // Take the last (newest) one
              const newestDuplicate = sortedDuplicates[sortedDuplicates.length - 1]
              console.log('Found newly duplicated quotation:', newestDuplicate.mserp_salesquotationnumber)
              setSelectedQuotationNumber(newestDuplicate.mserp_salesquotationnumber)
            } else {
              console.log('Could not find newly duplicated quotation')
            }
          }
        } catch (searchError) {
          console.error('Error searching for duplicated quotation:', searchError)
        }
      }

      // Show success message
      console.log('Quotation and lines duplicated successfully!')

    } catch (error) {
      console.error('Error duplicating quotation:', error)
      setError(t('quotation:list.failedToDuplicate'))
    } finally {
      setLoading(false)
      setShowDuplicateDialog(false)
      setDuplicatingQuote(null)
      setDuplicationProgress('')
    }
  }

  const cancelDuplicateQuote = () => {
    setShowDuplicateDialog(false)
    setDuplicatingQuote(null)
    setDuplicateCustomerAccount('')
    setDuplicateCustomerName('')
    setDuplicateIsNewCustomerOrProspect(false)
    setIsResolvingDuplicateCustomer(false)
    setDuplicateQuotationNumber('')
    setDuplicateQuotationName('')
    setDuplicateSelectedType(null)
    setDuplicateRecalculatePrices(false)
    setDuplicateError(null) // Clear duplicate dialog error
  }

  const handleDeleteQuote = (quote) => {
    setDeletingQuote(quote)
    setShowDeleteDialog(true)
  }

  const confirmDeleteQuote = async () => {
    if (!deletingQuote) return

    // Close the dialog immediately
    setShowDeleteDialog(false)
    const quotationToDelete = deletingQuote
    setDeletingQuote(null)

    // Delete in background
    try {
      console.log('Deleting quotation in background:', quotationToDelete.mserp_salesquotationnumber)

      // Call the service to delete the quotation (including lines)
      const result = await dataService.deleteQuotation(quotationToDelete.mserp_salesquotationnumber)

      if (!result.success) {
        throw new Error(result.error || t('quotation:list.failedToDelete'))
      }

      console.log('Quotation deleted successfully:', result.data)

      // Refresh the quotes list to remove the deleted quotation
      await loadQuotes()

      // Show success message
      console.log('Quotation and lines deleted successfully!')

    } catch (error) {
      console.error('Error deleting quotation:', error)
      setError(t('quotation:list.failedToDelete'))
    }
  }

  const cancelDeleteQuote = () => {
    setShowDeleteDialog(false)
    setDeletingQuote(null)
  }

  const renderOriginTag = (quote) => {
    const origin = getOriginMeta(quote.mserp_alg_salesquotationorigin)
    if (!origin) return null
    return (
      <span
        className={`text-xs px-1.5 py-0.5 rounded border ${origin.tagClass}`}
        title={t('quotation:list.originLabel')}
      >
        {t(`quotation:list.origin.${origin.key}`)}
      </span>
    )
  }

  const getStatusColor = (status) => {
    if (!status || typeof status !== 'string') {
      return 'bg-white text-black border border-black'
    }

    switch (status.toLowerCase()) {
      case 'draft': return 'bg-white text-black border border-black'
      case 'sent': return 'bg-white text-black border border-black'
      case 'approved': return 'bg-red-500 text-white border border-red-500'
      case 'rejected': return 'bg-black text-white border border-black'
      default: return 'bg-white text-black border border-black'
    }
  }

  const getSalesStatusLabel = (statusValue) => {
    // Handle portal status (string values) or fallback to sales status (numeric values)
    if (typeof statusValue === 'string') {
      // Portal status specific mappings for better display
      const portalStatusMap = {
        'Draft': t('common:status.draft'),
        'SentToFnO': t('common:status.sentToErp'),
        'Sent': t('common:status.sent'),
        'Accepted': t('common:status.accepted'),
        'Rejected': t('common:status.rejected'),
        'Cancelled': t('common:status.cancelled')
      }

      return portalStatusMap[statusValue] || statusValue || t('common:status.unknown')
    }

    // Portal status mapping for numeric values (matching QuotationLinesView)
    const statusMap = {
      200000000: t('common:status.draft'),
      200000001: t('common:status.sentToErp'),
      200000002: t('common:status.sent'),
      200000003: t('common:status.accepted'),
      200000004: t('common:status.rejected'),
      200000005: t('common:status.cancelled')
    }

    return statusMap[statusValue] || t('common:status.unknown')
  }

  const isQuotationDraft = (statusValue) => {
    // Check if quotation is in Draft status
    if (typeof statusValue === 'string') {
      return statusValue === 'Draft'
    }
    // Check numeric status value for Draft (200000000)
    return statusValue === 200000000
  }

  const getSalesStatusTextColor = (statusValue) => {
    const statusLabel = getSalesStatusLabel(statusValue)

    switch (statusLabel.toLowerCase()) {
      // Portal status colors
      case 'draft': return 'text-gray-600'
      case 'sent to erp': return 'text-blue-600'
      case 'sent': return 'text-blue-600'
      case 'accepted': return 'text-green-600'
      case 'rejected': return 'text-red-600'
      case 'cancelled': return 'text-red-600'

      // Legacy sales status colors (fallback)
      case 'created': return 'text-neutral-600'
      case 'confirmed': return 'text-green-600'
      case 'submitted': return 'text-blue-600'
      case 'approved': return 'text-green-600'
      case 'lost': return 'text-red-600'
      case 'canceled': return 'text-neutral-500'
      case 'reset': return 'text-neutral-500'
      case 'modified': return 'text-orange-600'
      case 'revised': return 'text-orange-600'
      default: return 'text-neutral-600'
    }
  }

  const isPastDue = (dateString) => {
    // If date is empty/null, consider it past due (needs attention)
    if (!dateString) return true

    const date = new Date(dateString)
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Reset to start of day for accurate comparison

    return date < today
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  // Helper function to display customer or prospect with subtle indicator
  const getCustomerOrProspectDisplay = (quote) => {
    if (quote.mserp_requestingcustomeraccountnumber) {
      return {
        value: quote.mserp_requestingcustomeraccountnumber,
        type: 'customer',
        displayValue: quote.mserp_requestingcustomeraccountnumber
      }
    } else if (quote.mserp_prospectid) {
      return {
        value: quote.mserp_prospectid,
        type: 'prospect',
        displayValue: quote.mserp_prospectid
      }
    } else {
      return {
        value: 'N/A',
        type: 'none',
        displayValue: 'N/A'
      }
    }
  }

  // Group quotations by opportunity ID for the opportunity view
  const groupQuotationsByOpportunity = (quotes) => {
    if (currentView !== 'opportunity') {
      return quotes.map(quote => ({ type: 'quote', quote }))
    }

    const opportunityGroups = {}
    const ungroupedQuotes = []

    quotes.forEach(quote => {
      const opportunityId = quote.mserp_alg_portalopportunity
      if (opportunityId) {
        if (!opportunityGroups[opportunityId]) {
          opportunityGroups[opportunityId] = []
        }
        opportunityGroups[opportunityId].push(quote)
      } else {
        ungroupedQuotes.push(quote)
      }
    })

    const result = []

    // Add grouped opportunities
    Object.entries(opportunityGroups).forEach(([opportunityId, quotes]) => {
      if (quotes.length > 1) {
        // Multiple quotes for this opportunity - show as group
        result.push({
          type: 'group',
          opportunityId,
          quotes,
          isExpanded: expandedOpportunities.has(opportunityId)
        })
      } else {
        // Single quote - show normally
        result.push({ type: 'quote', quote: quotes[0] })
      }
    })

    // Add ungrouped quotes
    ungroupedQuotes.forEach(quote => {
      result.push({ type: 'quote', quote })
    })

    return result
  }

  const toggleOpportunityGroup = (opportunityId) => {
    setExpandedOpportunities(prev => {
      const newSet = new Set(prev)
      if (newSet.has(opportunityId)) {
        newSet.delete(opportunityId)
      } else {
        newSet.add(opportunityId)
      }
      return newSet
    })
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="font-bold uppercase tracking-wider text-lg">{t('quotation:list.title')}</span>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadQuotes()}
                disabled={loading}
              >
                {loading ? t('common:loading.refreshing') : t('common:actions.refresh')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
              >
                {t('common:actions.clearFilters')}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t('quotation:list.searchLabel')}
              placeholder={t('quotation:list.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            {!hideEmailFilter && (
              <Input
                type="email"
                label={t('quotation:list.creatorEmailFilterLabel')}
                placeholder={t('quotation:list.creatorEmailFilterPlaceholder')}
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
              />
            )}
          </div>
          <div className="mt-2 text-sm text-brand-darkText">
            {t('quotation:list.searchHint')}
          </div>

          {/* Opportunity Filter - only show when Opportunity view is selected */}
          {currentView === 'opportunity' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                value={opportunityFilter}
                onChange={(e) => setOpportunityFilter(e.target.value)}
                className="border-blue-200 focus:border-blue-500 focus:ring-blue-200"
              >
                <option value="">{t('quotation:list.allOpportunities')}</option>
                {availableOpportunities.map(opportunity => (
                  <option key={opportunity} value={opportunity}>
                    {opportunity}
                  </option>
                ))}
              </Select>
              <div className="text-sm text-blue-600 flex items-center">
                {t('quotation:list.filterByOpportunity')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quotes List */}
      <Card variant="neumorphic">
        <CardHeader>
          <CardTitle className="flex items-center flex-wrap gap-x-4 gap-y-3">
            <span>{t('quotation:list.salesQuotations')}</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setCurrentView('open')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  currentView === 'open'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('quotation:list.open')}
              </button>
              <button
                onClick={() => setCurrentView('closed')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  currentView === 'closed'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('quotation:list.closed')}
              </button>
              <button
                onClick={() => setCurrentView('followup')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  currentView === 'followup'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('quotation:list.followUp')}
              </button>
              <button
                onClick={() => setCurrentView('expiry')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  currentView === 'expiry'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('quotation:list.expiry')}
              </button>
              <button
                onClick={() => setCurrentView('opportunity')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  currentView === 'opportunity'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t('quotation:list.opportunity')}
              </button>
            </div>

            {/* Origin filter - combined (AND) with the view on the left. Right-aligned; wraps under on narrow widths */}
            <div
              role="group"
              aria-label={t('quotation:list.originLabel')}
              className="flex items-center gap-2 flex-wrap ml-auto"
            >
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wide mr-1">
              {t('quotation:list.originLabel')}
            </span>
            <button
              type="button"
              aria-pressed={originFilter === ''}
              onClick={() => setOriginFilter('')}
              className={`${ORIGIN_PILL_BASE} ${
                originFilter === '' ? 'bg-gray-100 border-gray-500 text-gray-900 font-semibold' : ORIGIN_PILL_IDLE
              }`}
            >
              {t('quotation:list.origin.all')}
            </button>
            {QUOTATION_ORIGINS.map(origin => {
              const selected = originFilter === origin.value
              return (
                <button
                  key={origin.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setOriginFilter(prev => prev === origin.value ? '' : origin.value)}
                  className={`${ORIGIN_PILL_BASE} ${selected ? `${origin.selectedClass} font-semibold` : ORIGIN_PILL_IDLE}`}
                >
                  <span aria-hidden="true" className={`inline-block w-2 h-2 rounded-full ${origin.dotClass}`}></span>
                  {t(`quotation:list.origin.${origin.key}`)}
                </button>
              )
            })}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              <span className="ml-3 text-brand-darkText">{t('quotation:list.loadingQuotes')}</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-500 rounded-sm p-4 mb-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {!loading && !error && quotes.length === 0 && (
            <div className="text-center py-8">
              <p className="text-brand-darkText">{t('quotation:list.noQuotes')}</p>
            </div>
          )}

          {!loading && !error && quotes.length > 0 && (
            <div className="space-y-4">
              <AnimatePresence>
                {groupQuotationsByOpportunity(quotes).map((item, index) => {
                  if (item.type === 'group') {
                    // Render group header
                    return (
                      <motion.div
                        key={`group-${item.opportunityId}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="space-y-2"
                      >
                        {/* Group Header */}
                        <div
                          className="bg-blue-50 rounded-sm p-4 border border-blue-200 hover:border-blue-400 hover:shadow-sm transition-all duration-200 cursor-pointer"
                          onClick={() => toggleOpportunityGroup(item.opportunityId)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2">
                                <svg
                                  className={`w-4 h-4 text-blue-600 transition-transform ${item.isExpanded ? 'rotate-90' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="text-sm font-medium text-blue-600">{t('quotation:list.opportunityGroup')}</span>
                              </div>
                              <div>
                                <p className="font-semibold text-blue-800">{item.opportunityId}</p>
                                <p className="text-sm text-blue-600">{t('quotation:list.quotationsCount', { count: item.quotes.length })}</p>
                              </div>
                            </div>
                            <div className="text-sm text-blue-600">
                              {item.isExpanded ? t('quotation:list.clickToCollapse') : t('quotation:list.clickToExpand')}
                            </div>
                          </div>
                        </div>

                        {/* Group Content - Show when expanded */}
                        <AnimatePresence>
                          {item.isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="space-y-2 pl-4"
                            >
                              {item.quotes.map((quote, quoteIndex) => (
                                <motion.div
                                  key={quote.mserp_salesquotationheaderv2entityid || quote.mserp_salesquotationnumber || `grouped-quote-${quoteIndex}`}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -20 }}
                                  transition={{ delay: quoteIndex * 0.05 }}
                                  className="bg-white rounded-sm p-6 border border-gray-300 hover:border-brand-primary hover:shadow-sm transition-all duration-200 relative"
                                >
                                  <div className="space-y-4">
                                    {/* Info Grid - Responsive Layout */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:grid-cols-5">
                                      <div>
                                        <p className="text-sm text-gray-600">{t('common:labels.quotationNumber')}</p>
                                        <h4 className="font-semibold text-brand-darkText mb-1 flex items-center gap-2">
                                          <span>{quote.mserp_salesquotationnumber || 'N/A'}</span>
                                          {renderOriginTag(quote)}
                                        </h4>
                                      </div>

                                      <div>
                                        <p className="text-sm text-gray-600">{t('common:labels.quotationName')}</p>
                                        <p className="font-medium text-brand-darkText">{getQuotationName(quote) || 'N/A'}</p>
                                      </div>

                                      <div>
                                        <p className="text-sm text-gray-600">{t('common:labels.customerAccount')}</p>
                                        {(() => {
                                          const customerInfo = getCustomerOrProspectDisplay(quote)
                                          return (
                                            <div className="flex items-center gap-2">
                                              <p className="font-medium text-brand-darkText">{customerInfo.displayValue}</p>
                                              {customerInfo.type === 'prospect' && (
                                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded border border-blue-200">
                                                  P
                                                </span>
                                              )}
                                            </div>
                                          )
                                        })()}
                                      </div>

                                      <div>
                                        <p className="text-sm text-gray-600">{t('common:labels.totalLineAmount')}</p>
                                        <p className="font-medium text-brand-darkText">
                                          {quote.mserp_alg_totallineamount ?
                                            new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(quote.mserp_alg_totallineamount)
                                            : '0.00'}
                                        </p>
                                      </div>

                                      <div>
                                        <p className="text-sm text-gray-600">{t('common:labels.status')}</p>
                                        <div className="flex items-center gap-20">
                                          <p className={`font-medium ${getSalesStatusTextColor(quote.mserp_alg_portalstatus)}`}>
                                            {getSalesStatusLabel(quote.mserp_alg_portalstatus)}
                                          </p>
                                          {/* Show "Past Due" badge in Follow-up or Expiry views if date is past due */}
                                          {currentView === 'followup' && isPastDue(quote.mserp_salesquotationfollowupdate) && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                              </svg>
                                              {t('common:status.pastDue')}
                                            </span>
                                          )}
                                          {currentView === 'expiry' && isPastDue(quote.mserp_salesquotationexpirydate) && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                              </svg>
                                              {t('common:status.pastDue')}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Action Dropdown - Top Right Corner */}
                                    <div className="absolute top-4 right-4">
                                      <div className="relative">
                                        <button
                                          onClick={() => setOpenDropdown(openDropdown === `grouped-${index}-${quoteIndex}` ? null : `grouped-${index}-${quoteIndex}`)}
                                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                        >
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                          </svg>
                                        </button>

                                        {openDropdown === `grouped-${index}-${quoteIndex}` && (
                                          <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                            <div className="py-1">
                                              <button
                                                onClick={() => {
                                                  setSelectedQuotationNumber(quote.mserp_salesquotationnumber)
                                                  setOpenDropdown(null)
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                              >
                                                {t('common:actions.details')}
                                              </button>
                                              {allowQuotationDuplicate && (
                                                <button
                                                  onClick={() => {
                                                    handleDuplicateQuote(quote)
                                                    setOpenDropdown(null)
                                                  }}
                                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                  {t('common:actions.duplicate')}
                                                </button>
                                              )}
                                              {/* View Report - only for Sent/Accepted/Cancelled/Rejected with report URL */}
                                              {quote.mserp_alg_salesquotationreporturl &&
                                               (quote.mserp_alg_portalstatus === 200000001 || // Sent to ERP
                                                quote.mserp_alg_portalstatus === 200000002 || // Sent
                                                quote.mserp_alg_portalstatus === 200000003 || // Accepted
                                                quote.mserp_alg_portalstatus === 200000004 || // Rejected
                                                quote.mserp_alg_portalstatus === 200000005) && ( // Cancelled
                                                <>
                                                  <button
                                                    onClick={() => {
                                                      const baseUrl = 'https://spvg.sharepoint.com/sites/FO_DOCS/Shared%20Documents/'
                                                      const fullUrl = baseUrl + quote.mserp_alg_salesquotationreporturl
                                                      window.open(fullUrl, '_blank')
                                                      setOpenDropdown(null)
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900"
                                                  >
                                                    {t('quotation:list.viewReport')}
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      const baseUrl = 'https://spvg.sharepoint.com/sites/FO_DOCS/Shared%20Documents/'
                                                      const fullUrl = baseUrl + quote.mserp_alg_salesquotationreporturl
                                                      navigator.clipboard.writeText(fullUrl).then(() => {
                                                        // Optional: Show a toast notification here
                                                        console.log('Report URL copied to clipboard')
                                                      }).catch(err => {
                                                        console.error('Failed to copy URL:', err)
                                                      })
                                                      setOpenDropdown(null)
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900"
                                                  >
                                                    {t('quotation:list.copyReportUrl')}
                                                  </button>
                                                </>
                                              )}
                                              {isQuotationDraft(quote.mserp_alg_portalstatus) && isQuotationDraft(quote.mserp_salesquotationstatus) && (
                                                <button
                                                  onClick={() => {
                                                    handleDeleteQuote(quote)
                                                    setOpenDropdown(null)
                                                  }}
                                                  className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 hover:text-red-900"
                                                >
                                                  {t('common:actions.delete')}
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )
                  } else {
                    // Render individual quote
                    const quote = item.quote
                    return (
                      <motion.div
                        key={quote.mserp_salesquotationheaderv2entityid || quote.mserp_salesquotationnumber || `quote-${index}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white rounded-sm p-6 border border-gray-300 hover:border-brand-primary hover:shadow-sm transition-all duration-200 relative"
                      >
                        <div className="space-y-4">
                          {/* Info Grid - Responsive Layout */}
                          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${currentView === 'opportunity' ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}>
                            <div>
                              <p className="text-sm text-gray-600">{t('common:labels.quotationNumber')}</p>
                              <h4 className="font-semibold text-brand-darkText mb-1 flex items-center gap-2">
                                <span>{quote.mserp_salesquotationnumber || 'N/A'}</span>
                                {renderOriginTag(quote)}
                              </h4>
                            </div>

                            <div>
                              <p className="text-sm text-gray-600">{t('common:labels.quotationName')}</p>
                              <p className="font-medium text-brand-darkText">{getQuotationName(quote) || 'N/A'}</p>
                            </div>

                            {/* Show Opportunity ID when in opportunity view */}
                            {currentView === 'opportunity' && (
                              <div>
                                <p className="text-sm text-gray-600">{t('common:labels.opportunityId')}</p>
                                <p className="font-medium text-blue-600">{quote.mserp_alg_portalopportunity || 'N/A'}</p>
                              </div>
                            )}

                            <div>
                              <p className="text-sm text-gray-600">{t('common:labels.customerAccount')}</p>
                              {(() => {
                                const customerInfo = getCustomerOrProspectDisplay(quote)
                                return (
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-brand-darkText">{customerInfo.displayValue}</p>
                                    {customerInfo.type === 'prospect' && (
                                      <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded border border-blue-200">
                                        P
                                      </span>
                                    )}
                                  </div>
                                )
                              })()}
                            </div>

                            <div>
                              <p className="text-sm text-gray-600">{t('common:labels.totalLineAmount')}</p>
                              <p className="font-medium text-brand-darkText">
                                {quote.mserp_alg_totallineamount ?
                                  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(quote.mserp_alg_totallineamount)
                                  : '0.00'}
                              </p>
                            </div>

                            <div>
                              <p className="text-sm text-gray-600">{t('common:labels.status')}</p>
                              <div className="flex items-center gap-20">
                                <p className={`font-medium ${getSalesStatusTextColor(quote.mserp_alg_portalstatus)}`}>
                                  {getSalesStatusLabel(quote.mserp_alg_portalstatus)}
                                </p>
                                {/* Show "Past Due" badge in Follow-up or Expiry views if date is past due */}
                                {currentView === 'followup' && isPastDue(quote.mserp_salesquotationfollowupdate) && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                    {t('common:status.pastDue')}
                                  </span>
                                )}
                                {currentView === 'expiry' && isPastDue(quote.mserp_salesquotationexpirydate) && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                    {t('common:status.pastDue')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Action Dropdown - Top Right Corner */}
                          <div className="absolute top-4 right-4">
                            <div className="relative">
                              <button
                                onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              {openDropdown === index && (
                                <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        setSelectedQuotationNumber(quote.mserp_salesquotationnumber)
                                        setOpenDropdown(null)
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      {t('common:actions.details')}
                                    </button>
                                    {allowQuotationDuplicate && (
                                      <button
                                        onClick={() => {
                                          handleDuplicateQuote(quote)
                                          setOpenDropdown(null)
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      >
                                        {t('common:actions.duplicate')}
                                      </button>
                                    )}
                                    {/* View Report - only for Sent/Accepted/Cancelled/Rejected with report URL */}
                                    {quote.mserp_alg_salesquotationreporturl &&
                                     (quote.mserp_alg_portalstatus === 200000001 || // Sent to ERP
                                      quote.mserp_alg_portalstatus === 200000002 || // Sent
                                      quote.mserp_alg_portalstatus === 200000003 || // Accepted
                                      quote.mserp_alg_portalstatus === 200000004 || // Rejected
                                      quote.mserp_alg_portalstatus === 200000005) && ( // Cancelled
                                      <>
                                        <button
                                          onClick={() => {
                                            const baseUrl = 'https://spvg.sharepoint.com/sites/FO_DOCS/Shared%20Documents/'
                                            const fullUrl = baseUrl + quote.mserp_alg_salesquotationreporturl
                                            window.open(fullUrl, '_blank')
                                            setOpenDropdown(null)
                                          }}
                                          className="block w-full text-left px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900"
                                        >
                                          {t('quotation:list.viewReport')}
                                        </button>
                                        <button
                                          onClick={() => {
                                            const baseUrl = 'https://spvg.sharepoint.com/sites/FO_DOCS/Shared%20Documents/'
                                            const fullUrl = baseUrl + quote.mserp_alg_salesquotationreporturl
                                            navigator.clipboard.writeText(fullUrl).then(() => {
                                              // Optional: Show a toast notification here
                                              console.log('Report URL copied to clipboard')
                                            }).catch(err => {
                                              console.error('Failed to copy URL:', err)
                                            })
                                            setOpenDropdown(null)
                                          }}
                                          className="block w-full text-left px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900"
                                        >
                                          {t('quotation:list.copyReportUrl')}
                                        </button>
                                      </>
                                    )}
                                    {isQuotationDraft(quote.mserp_alg_portalstatus) && isQuotationDraft(quote.mserp_salesquotationstatus) && (
                                      <button
                                        onClick={() => {
                                          handleDeleteQuote(quote)
                                          setOpenDropdown(null)
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 hover:text-red-900"
                                      >
                                        {t('common:actions.delete')}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )
                  }
                })}
              </AnimatePresence>
            </div>
          )}

          {!loading && !error && quotes.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-300">
              <div className="text-center text-sm text-brand-darkText">
                {t('quotation:list.showingCount', { count: quotes.length })}
                {/* Pagination temporarily disabled due to Dataverse $skip limitation */}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quotation Lines Modal */}
      {selectedQuotationNumber && (
        <QuotationLinesView
          quotationNumber={selectedQuotationNumber}
          onClose={() => setSelectedQuotationNumber(null)}
          onSave={() => {
            // No need to refresh quotations list when only lines are saved
            console.log('Quotation lines saved successfully')
          }}
        />
      )}

      {/* Duplicate Confirmation Dialog */}
      {showDuplicateDialog && allowQuotationDuplicate && createPortal(
        <motion.div
          className="fixed top-0 left-0 w-full h-full bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{
            zIndex: 99999,
            position: 'fixed !important',
            top: '0 !important',
            left: '0 !important',
            right: '0 !important',
            bottom: '0 !important',
            width: '100vw !important',
            height: '100vh !important',
            margin: '0 !important',
            padding: '16px !important'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => !loading && e.target === e.currentTarget && cancelDuplicateQuote()}
        >
          <motion.div
            className="bg-brand-contentBg rounded-sm p-6 max-w-md w-full shadow-lg border border-gray-300"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-sm flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📋</span>
              </div>
              <h3 className="text-lg font-bold text-brand-darkText mb-4">
                {t('quotation:duplicate.title')}
              </h3>
              <div className="text-left space-y-4 mb-6">
                <CustomerOrProspectLookup
                  label={t('quotation:duplicate.customerLabel')}
                  value={duplicateCustomerAccount}
                  onChange={setDuplicateCustomerAccount}
                  onTypeChange={setDuplicateSelectedType}
                  onSelectionChange={(item) => {
                    setDuplicateCustomerName(item?.name || '')
                    setDuplicateIsNewCustomerOrProspect(item?.isNew === true)
                  }}
                  onResolvingChange={setIsResolvingDuplicateCustomer}
                  placeholder={t('quotation:form.customerPlaceholder')}
                  className=""
                />

                {canSetManualQuotationNumber && (
                  <div>
                    <label htmlFor="duplicate-number" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('quotation:duplicate.numberLabel')} <span className="text-xs text-gray-500">{t('quotation:duplicate.numberHint')}</span>
                    </label>
                    <Input
                      id="duplicate-number"
                      type="text"
                      value={duplicateQuotationNumber}
                      onChange={(e) => setDuplicateQuotationNumber(e.target.value.slice(0, 20))}
                      disabled={loading}
                      placeholder={t('quotation:duplicate.numberPlaceholder')}
                      maxLength={20}
                      required
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="duplicate-name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('quotation:duplicate.nameLabel')}
                  </label>
                  <Input
                    id="duplicate-name"
                    type="text"
                    value={duplicateQuotationName}
                    onChange={(e) => setDuplicateQuotationName(e.target.value)}
                    disabled={loading}
                    placeholder={t('quotation:duplicate.namePlaceholder')}
                    required
                  />
                </div>

                <label htmlFor="duplicate-recalculate-prices" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    id="duplicate-recalculate-prices"
                    type="checkbox"
                    checked={duplicateRecalculatePrices}
                    onChange={(e) => setDuplicateRecalculatePrices(e.target.checked)}
                    disabled={loading}
                    className="h-4 w-4 rounded border-gray-300 accent-brand-primary focus:ring-brand-primary"
                  />
                  {t('quotation:duplicate.recalculatePrices')}
                </label>

                <p className="text-sm text-gray-600">
                  {t('quotation:duplicate.description')}
                </p>
              </div>
              {duplicateError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm">{duplicateError}</p>
                  </div>
                </div>
              )}
              {duplicationProgress && (
                <div className="mb-4">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary"></div>
                    <span className="text-sm text-brand-primary">{duplicationProgress}</span>
                  </div>
                </div>
              )}
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={cancelDuplicateQuote}
                  disabled={loading}
                  className="flex-1"
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  onClick={confirmDuplicateQuote}
                  disabled={loading || !duplicateCustomerAccount || !duplicateCustomerName.trim() || isResolvingDuplicateCustomer || (canSetManualQuotationNumber && !duplicateQuotationNumber.trim()) || !duplicateQuotationName.trim()}
                  className="flex-1"
                >
                  {loading ? t('quotation:duplicate.duplicating') : t('quotation:duplicate.duplicateButton')}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && createPortal(
        <motion.div
          className="fixed top-0 left-0 w-full h-full bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{
            zIndex: 99999,
            position: 'fixed !important',
            top: '0 !important',
            left: '0 !important',
            right: '0 !important',
            bottom: '0 !important',
            width: '100vw !important',
            height: '100vh !important',
            margin: '0 !important',
            padding: '16px !important'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && cancelDeleteQuote()}
        >
          <motion.div
            className="bg-brand-contentBg rounded-sm p-6 max-w-md w-full shadow-lg border border-gray-300"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-sm flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-red-600">🗑️</span>
              </div>
              <h3 className="text-lg font-bold text-brand-darkText mb-2">
                {t('quotation:deleteDialog.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('quotation:deleteDialog.confirmMessage', { name: getQuotationName(deletingQuote) })}
                <br />
                <br />
                <span className="text-red-600 font-medium">{t('quotation:deleteDialog.warningMessage')}</span> {t('quotation:deleteDialog.permanentNote')}
              </p>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={cancelDeleteQuote}
                  disabled={loading}
                  className="flex-1"
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  onClick={confirmDeleteQuote}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? t('quotation:deleteDialog.deleting') : t('quotation:deleteDialog.deleteButton')}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>,
        document.body
      )}
    </div>
  )
}
